import React, { type ReactNode } from 'react';
import { navItemType, type SecondLevelNavItem, type SiteConfig } from '@cx-web/domain-types';

import { mergePersistentParams } from '../../utils/merge-persistent-params';
import { ExpandableSecondaryNav } from '../ExpandableSecondaryNav/index';
import { HeaderNavItem } from '../HeaderNavItem/index';
import { ScrollableNav } from '../ScrollableNav/index';

import styles from './SecondaryHeaderNavGroup.module.css';

const isRightAlignedDropdown = (type: SecondLevelNavItem['type']): boolean =>
  type === navItemType.ALL_SPORTS_EXPANDABLE || type === navItemType.SPLIT_EXPANDABLE;

type SecondaryHeaderNavGroupProps = {
  betslipSlot?: ReactNode;
  bookmakers: SiteConfig['bookmakers'];
  isUkStyle?: boolean;
  navigationKey?: string;
  renderLogo?: ReactNode;
  repubHomeUrl: string;
  secondLevelNav: SecondLevelNavItem[];
  serverUrl: string;
};

const SecondaryHeaderNavGroup = ({
  betslipSlot,
  bookmakers,
  isUkStyle,
  navigationKey,
  renderLogo,
  repubHomeUrl,
  secondLevelNav,
  serverUrl,
}: SecondaryHeaderNavGroupProps) => {
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const requestUrl = serverUrl;

  const navWithoutBetslip = secondLevelNav.filter(item => item.type !== navItemType.BETSLIP);
  const shouldRenderBetslip = navWithoutBetslip.length !== secondLevelNav.length;

  const quickLinks = isUkStyle
    ? navWithoutBetslip.filter(item => !isRightAlignedDropdown(item.type))
    : navWithoutBetslip;
  const rightRailDropdowns = isUkStyle ? navWithoutBetslip.filter(item => isRightAlignedDropdown(item.type)) : [];
  const showRightRail = isUkStyle && (rightRailDropdowns.length > 0 || shouldRenderBetslip);

  return (
    <section className={styles.container} data-testid="secondary-header-nav-section">
      <nav className={styles.content}>
        <a href={mergePersistentParams(repubHomeUrl, requestUrl)}>
          <div className={styles.logoWrapper}>{renderLogo}</div>
        </a>
        {isUkStyle ? (
          <ExpandableSecondaryNav
            bookmakers={bookmakers}
            dropdownRef={dropdownRef}
            isUkStyle={isUkStyle}
            links={quickLinks}
            serverUrl={serverUrl}
          />
        ) : (
          <ScrollableNav
            autoScrollToSelected
            navigationKey={navigationKey}
            style={
              {
                '--fade-bg-color': 'var(--cxy-color-midnight-blue-600)',
                '--nav-bg-color': 'var(--cxy-color-midnight-blue-600)',
              } as React.CSSProperties
            }
            testId="secondary-header-nav"
          >
            {quickLinks.map((link, index) => (
              <React.Fragment key={`${link.link}-${index}`}>
                <HeaderNavItem
                  {...link}
                  bookmakers={bookmakers}
                  dropdownRef={dropdownRef}
                  isUkStyle={isUkStyle}
                  serverUrl={serverUrl}
                  variant="secondary"
                />
              </React.Fragment>
            ))}
          </ScrollableNav>
        )}
        {showRightRail && (
          <ul className={styles.rightRail} data-testid="secondary-header-right-rail">
            {rightRailDropdowns.map((link, index) => (
              <React.Fragment key={`${link.name}-${index}`}>
                <HeaderNavItem
                  {...link}
                  bookmakers={bookmakers}
                  dropdownRef={dropdownRef}
                  isUkStyle={isUkStyle}
                  serverUrl={serverUrl}
                  variant="secondary"
                />
              </React.Fragment>
            ))}
            {shouldRenderBetslip ? (
              <li className={styles.betslipSlot} data-testid="secondary-header-betslip-slot">
                {betslipSlot}
              </li>
            ) : null}
          </ul>
        )}
        <div className={styles.dropdownWrapper} ref={dropdownRef} />
      </nav>
    </section>
  );
};

export { SecondaryHeaderNavGroup };
